import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
} from "@mui/material";
import moment from "moment";

import TextFieldController from "../../components/inputs/TextFieldController";
import DropdownController from "../../components/inputs/DropdownController";
import DatePickerController from "../../components/inputs/DatePickerController";
import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import { isValidDateTest, maxLengthMessage } from "../../utils/functions";

const issueSchema = yup.object().shape({
	date: isValidDateTest("Date").required("Date is required"),
	quantity: yup
		.number()
		.nullable()
		.required("Quantity is required")
		.transform((v, o) => (o === "" ? null : v))
		.min(1, "Quantity must be at least 1")
		.test("max-stock", "Exceeds available stock", function (value) {
			const maxStock = this.options.context?.maxStock;
			if (maxStock == null) return true;
			return value <= maxStock;
		}),
	employeeID: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	vehicleID: yup
		.number()
		.nullable()
		.transform((v, o) => (o === "" ? null : v)),
	serialId: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
	issuedBy: yup.string().trim().max(100, maxLengthMessage(100)).nullable(),
	comments: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
});

const buildDefaults = () => ({
	date: moment().format("MM/DD/YYYY"),
	quantity: "",
	employeeID: "",
	vehicleID: "",
	serialId: "",
	issuedBy: "",
	comments: "",
});

const IssueItemDialog = ({
	open,
	onClose,
	onSave,
	issueItem,
	employeeOptions = [],
	vehicleOptions = [],
}) => {
	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(issueSchema),
		defaultValues: buildDefaults(),
		context: { maxStock: issueItem?.unitsInStock || 0 },
	});

	useEffect(() => {
		if (open) {
			reset(buildDefaults());
		}
	}, [open, reset]);

	const quantity = watch("quantity");
	const unitsInStock = issueItem?.unitsInStock || 0;
	const unitCost = issueItem?.cost != null ? Number(issueItem.cost) : null;
	const parsedQty = Number(quantity);
	const exceedsStock = parsedQty > unitsInStock;
	const showTotalCost = unitCost != null && parsedQty > 0 && !exceedsStock;

	const stockColor = () => {
		if (unitsInStock <= 0) return "error.main";
		if (unitsInStock <= 5) return "warning.main";
		return "success.main";
	};

	const onSubmit = (data) => {
		const payload = {
			date: data.date
				? moment(data.date, "MM/DD/YYYY").format("YYYY-MM-DD")
				: null,
			quantity: data.quantity,
			employeeID: data.employeeID || null,
			vehicleID: data.vehicleID || null,
			serialId: data.serialId?.trim() || null,
			issuedBy: data.issuedBy?.trim() || null,
			comments: data.comments?.trim() || null,
		};
		onSave(payload);
		reset();
		onClose();
	};

	if (!issueItem) return null;

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>
				{`Issue — ${issueItem.trackingNumber} · ${issueItem.description}`}
			</DialogTitle>

			<DialogContent>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					{/* ── Item Summary Banner ── */}
					<Box
						sx={{
							display: "flex",
							gap: 2,
							p: "10px 14px",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
							alignItems: "center",
							mb: 2,
							mt: 1,
							bgcolor: "action.hover",
						}}
					>
						<Typography sx={{ fontSize: 22 }}>📦</Typography>
						<Box>
							<Typography variant="body2" fontWeight={700}>
								{issueItem.description}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								In Stock:{" "}
								<Typography
									component="span"
									variant="caption"
									fontWeight={700}
									color={stockColor()}
								>
									{unitsInStock}
								</Typography>
								{unitCost != null && (
									<Typography
										component="span"
										variant="caption"
										sx={{ ml: 1.5 }}
									>
										Unit Cost:{" "}
										<strong>${unitCost.toFixed(2)}</strong>
									</Typography>
								)}
							</Typography>
						</Box>
					</Box>

					{/* ── Form Fields ── */}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(2, 1fr)",
							gap: 2,
						}}
					>
						<Box>
							<DatePickerController
								name="date"
								label="Date"
								control={control}
								errors={errors}
								required
							/>
						</Box>

						<Box>
							<TextFieldController
								name="quantity"
								label="Quantity"
								control={control}
								errors={errors}
								type="number"
								required
								inputProps={{
									min: 1,
									max: unitsInStock,
								}}
							/>
							{exceedsStock && parsedQty > 0 && (
								<Typography
									variant="caption"
									color="error"
									fontWeight={700}
									sx={{ mt: 0.5, display: "block" }}
								>
									⚠️ Exceeds available stock ({unitsInStock})
								</Typography>
							)}
							{showTotalCost && (
								<Typography
									variant="caption"
									color="success.main"
									sx={{ mt: 0.5, display: "block" }}
								>
									Total cost: $
									{(parsedQty * unitCost).toFixed(2)}
								</Typography>
							)}
						</Box>

						<Box>
							<DropdownController
								name="employeeID"
								label="Assign to Employee"
								options={employeeOptions}
								control={control}
								errors={errors}
								placeholder="— None —"
							/>
						</Box>

						<Box>
							<DropdownController
								name="vehicleID"
								label="Assign to Vehicle"
								options={vehicleOptions}
								control={control}
								errors={errors}
								placeholder="— None —"
							/>
						</Box>

						<Box>
							<TextFieldController
								name="serialId"
								label="Serial / ID"
								control={control}
								errors={errors}
								placeholder="Serial number, badge #, asset tag…"
							/>
						</Box>

						<Box>
							<TextFieldController
								name="issuedBy"
								label="Issued By"
								control={control}
								errors={errors}
								placeholder="Name of person issuing…"
							/>
						</Box>

						<Box sx={{ gridColumn: "1 / -1" }}>
							<TextFieldController
								name="comments"
								label="Comments"
								control={control}
								errors={errors}
								placeholder="Usage notes, reason for issuance…"
								multiline
								rows={2}
							/>
						</Box>
					</Box>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATSaveButton
					onClick={handleSubmit(onSubmit)}
					tooltipText="Confirm Issue"
					disabled={exceedsStock}
				/>
				<PATCloseButton onClick={onClose} tooltipText="Cancel" />
			</DialogActions>
		</Dialog>
	);
};

export default IssueItemDialog;
